/**
 * The drawn frames' times for the performance monitor (ADR-087 decision 2): Phaser's `game.loop.delta` of each frame,
 * kept in a ring of the last `FRAME_SAMPLES` while the panel shows. Recording is one typed-array store; the summary
 * sorts a preallocated copy, which only the monitor's 2 Hz update asks for, so nothing is allocated per frame.
 */

/**
 * Frames kept: four seconds at 60 frames a second.
 */
export const FRAME_SAMPLES = 240;

/**
 * Frames per second and the frame time's median and 99th percentile in milliseconds, all 0 before any frame.
 */
export interface FrameSummary {
  readonly fps: number;
  readonly p50: number;
  readonly p99: number;
}

/**
 * The ring of frame times.
 */
export class FrameSampler {
  private readonly samples = new Float32Array(FRAME_SAMPLES);
  private readonly sorted = new Float32Array(FRAME_SAMPLES);
  private next = 0;
  private filled = 0;

  /**
   * Records one frame's time; a time that is not a positive finite number is skipped.
   *
   * @param deltaMs Milliseconds since the frame before.
   */
  public record(deltaMs: number): void {
    if (!(deltaMs > 0) || !Number.isFinite(deltaMs)) {
      return;
    }
    this.samples[this.next] = deltaMs;
    this.next = (this.next + 1) % FRAME_SAMPLES;
    this.filled = Math.min(this.filled + 1, FRAME_SAMPLES);
  }

  /**
   * Forgets every frame, as the panel hides, so a shown panel never reads frames from before it showed.
   */
  public clear(): void {
    this.next = 0;
    this.filled = 0;
  }

  /**
   * The frames held, summed up: frames a second from their mean time, and the median and 99th percentile by nearest
   * rank.
   *
   * @returns The summary.
   */
  public summary(): FrameSummary {
    const count = this.filled;
    if (count === 0) {
      return { fps: 0, p50: 0, p99: 0 };
    }
    const view = this.sorted.subarray(0, count);
    view.set(this.samples.subarray(0, count));
    view.sort();
    let total = 0;
    for (let i = 0; i < count; i += 1) {
      total += view[i] as number;
    }
    const at = (quantile: number): number => view[Math.min(count - 1, Math.max(0, Math.ceil(quantile * count) - 1))] as number;
    return { fps: (1000 * count) / total, p50: at(0.5), p99: at(0.99) };
  }
}
